<script setup>
import { ref, computed, nextTick, onBeforeUnmount } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';

import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useConversationStages } from 'dashboard/composables/useConversationStages';
import {
  CONVERSATION_STAGE_AND_LEGACY_LABELS,
  LEGACY_CONVERSATION_STAGE_LABEL_MAP,
  formatConversationStageLabel,
} from 'dashboard/constants/conversationStages';

const store = useStore();
const getters = useStoreGetters();
const { stageLabels } = useConversationStages();

const stageActionsRef = ref(null);
const arrowDownButtonRef = ref(null);
const dropdownStyle = ref({});
const isLoading = ref(false);
const showActionsDropdown = ref(false);

const removeOutsideClickListener = () => {
  document.removeEventListener('click', handleOutsideClick, true);
};

const closeDropdown = () => {
  showActionsDropdown.value = false;
  removeOutsideClickListener();
};

const positionDropdown = () => {
  const anchor = stageActionsRef.value;
  if (!anchor) return;

  const rect = anchor.getBoundingClientRect();
  dropdownStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 4}px`,
    right: `${Math.max(window.innerWidth - rect.right, 8)}px`,
  };
};

function handleOutsideClick(event) {
  if (stageActionsRef.value?.contains(event.target)) return;
  closeDropdown();
}

const openDropdown = async () => {
  showActionsDropdown.value = true;
  await nextTick();
  positionDropdown();
  removeOutsideClickListener();
  document.addEventListener('click', handleOutsideClick, true);
};

const toggleStageDropdown = () => {
  if (isLoading.value) return;
  if (showActionsDropdown.value) {
    closeDropdown();
  } else {
    openDropdown();
  }
};

const currentChat = computed(() => getters.getSelectedChat.value);

const currentStage = computed(() => {
  const labels = currentChat.value.labels || [];
  const stage = stageLabels.value.find(label => labels.includes(label));
  if (stage) return stage;

  const legacyStage = Object.entries(LEGACY_CONVERSATION_STAGE_LABEL_MAP).find(
    ([legacyLabel]) => labels.includes(legacyLabel)
  );

  return legacyStage?.[1];
});

const labelsToRemoveOnStageChange = computed(() => [
  ...new Set([...stageLabels.value, ...CONVERSATION_STAGE_AND_LEGACY_LABELS]),
]);

const moveToStage = async stage => {
  closeDropdown();
  isLoading.value = true;
  const previousLabels = currentChat.value.labels || [];
  const nextLabels = [
    ...previousLabels.filter(label => !labelsToRemoveOnStageChange.value.includes(label)),
    stage,
  ];
  const conversationId = currentChat.value.id;
  const conversationDisplayId =
    currentChat.value.display_id || currentChat.value.id;

  try {
    await store.dispatch('bulkActions/process', {
      type: 'Conversation',
      ids: [conversationDisplayId],
      labels: {
        remove: labelsToRemoveOnStageChange.value.filter(
          label => label !== stage
        ),
        add: [stage],
      },
    });
    await store.dispatch('updateConversation', {
      ...currentChat.value,
      id: conversationId,
      labels: [...new Set(nextLabels)],
      updated_at: Date.now() / 1000,
    });
    useAlert(`Etapa alterada para ${formatConversationStageLabel(stage)}`);
  } finally {
    isLoading.value = false;
  }
};

onBeforeUnmount(removeOutsideClickListener);
</script>

<template>
  <div
    ref="stageActionsRef"
    class="flex relative justify-end items-center resolve-actions z-[10000]"
    @click.stop
  >
    <ButtonGroup
      class="flex-shrink-0 rounded-lg shadow outline-1 outline outline-n-container"
    >
      <Button
        label="Etapa"
        size="sm"
        color="slate"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="toggleStageDropdown"
      />
      <Button
        ref="arrowDownButtonRef"
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="sm"
        no-animation
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        color="slate"
        trailing-icon
        @click="toggleStageDropdown"
      />
    </ButtonGroup>
    <Teleport to="body">
      <div
        v-if="showActionsDropdown"
        class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-[2147483647] bg-n-alpha-3 backdrop-blur-[100px] block max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
        :style="dropdownStyle"
        @click.stop
      >
        <WootDropdownMenu class="mb-0">
          <WootDropdownItem v-for="stage in stageLabels" :key="stage">
            <Button
              :label="
                currentStage === stage
                  ? `${formatConversationStageLabel(stage)} atual`
                  : formatConversationStageLabel(stage)
              "
              ghost
              slate
              sm
              start
              icon="i-lucide-list-checks"
              class="w-full capitalize"
              :disabled="currentStage === stage"
              @click="() => moveToStage(stage)"
            />
          </WootDropdownItem>
        </WootDropdownMenu>
      </div>
    </Teleport>
  </div>
</template>
